import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, X, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

// Features shown per plan card (i18n keys + which plans include them).
// Order matters for visual consistency.
const FEATURE_ROWS = [
  { key: "plan_feature_analyze", inc: { beta: true, professional: true, consulting: true } },
  { key: "plan_feature_history", inc: { beta: true, professional: true, consulting: true } },
  { key: "plan_feature_export", inc: { beta: true, professional: true, consulting: true } },
  { key: "plan_feature_trends", inc: { beta: false, professional: true, consulting: true } },
  { key: "plan_feature_compare", inc: { beta: false, professional: true, consulting: true } },
  { key: "plan_feature_alerts", inc: { beta: false, professional: true, consulting: true } },
  { key: "plan_feature_briefings", inc: { beta: false, professional: true, consulting: true } },
  { key: "plan_feature_press_kit", inc: { beta: true, professional: true, consulting: true } },
  { key: "plan_feature_consulting", inc: { beta: false, professional: false, consulting: true } },
  { key: "plan_feature_priority", inc: { beta: false, professional: true, consulting: true } },
];

function PlanCard({ planId, label, tagline, price, recommended, cta, locked, onAction, ctaTestId, isCurrent }) {
  const { t } = useLang();
  return (
    <div
      data-testid={`plan-card-${planId}`}
      className={`relative flex flex-col border ${
        recommended ? "border-[#D90429] border-2" : "border-[#0A0A0A]"
      } bg-white p-7 md:p-8`}
    >
      {recommended && (
        <div
          data-testid={`plan-${planId}-recommended`}
          className="absolute -top-3 left-7 px-2 py-1 bg-[#D90429] text-white font-mono-tech text-[9px] tracking-[0.25em]"
        >
          · {t("plan_recommended")}
        </div>
      )}
      <div className="font-mono-tech text-[9px] tracking-[0.3em] text-[#D90429] uppercase mb-2">
        · {planId}
      </div>
      <h3
        data-testid={`plan-${planId}-name`}
        className="font-serif-display text-3xl md:text-4xl font-bold leading-none mb-2"
      >
        {label}
      </h3>
      <p className="text-sm text-[#525252] mb-5 italic font-serif-display">{tagline}</p>

      <div className="flex items-baseline gap-1 mb-6 pb-6 border-b border-[#E5E5E5]">
        <span
          data-testid={`plan-${planId}-price`}
          className="font-serif-display text-5xl md:text-6xl font-bold tracking-tight"
        >
          {price}
        </span>
        <span className="text-2xl font-serif-display">€</span>
        <span className="font-mono-tech text-xs tracking-widest text-[#888] ml-1">
          {t("plan_per_month")}
        </span>
      </div>

      <ul className="space-y-3 mb-7 flex-1">
        {FEATURE_ROWS.map((row) => {
          const included = row.inc[planId];
          return (
            <li
              key={row.key}
              data-testid={`plan-${planId}-feature-${row.key}`}
              className="flex items-start gap-3 text-sm"
            >
              {included ? (
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D90429]" strokeWidth={3} />
              ) : (
                <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C4C4C4]" strokeWidth={2.5} />
              )}
              <span className={included ? "text-[#0A0A0A]" : "text-[#A3A3A3] line-through"}>
                {t(row.key)}
              </span>
            </li>
          );
        })}
      </ul>

      <button
        data-testid={ctaTestId}
        onClick={onAction}
        disabled={locked || isCurrent}
        className={`w-full py-4 font-mono-tech text-xs uppercase tracking-[0.2em] border transition-colors ${
          isCurrent
            ? "border-[#0A0A0A] bg-[#FAFAFA] text-[#0A0A0A] cursor-default"
            : recommended
            ? "border-[#D90429] bg-[#D90429] text-white hover:bg-[#A60023] hover:border-[#A60023]"
            : "border-[#0A0A0A] bg-white text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
        } ${locked && !isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {isCurrent ? `✓ ${t("plan_cta_current")}` : cta}
      </button>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [meBilling, setMeBilling] = useState(null);
  const [loading, setLoading] = useState(null); // planId currently being processed

  useEffect(() => {
    document.title = lang === "es" ? "Precios · GalascioDAS™" : "Pricing · GalascioDAS™";
  }, [lang]);

  useEffect(() => {
    if (!user) return;
    api
      .get("/billing/me")
      .then((r) => setMeBilling(r.data))
      .catch(() => setMeBilling(null));
  }, [user]);

  // Show toast if user was redirected from a canceled checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("canceled") === "1") {
      toast.info(t("billing_canceled_title"));
    }
  }, [location.search, t]);

  const startCheckout = async (planId) => {
    if (!user) {
      navigate("/login?next=/pricing");
      return;
    }
    setLoading(planId);
    try {
      const res = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Stripe did not return a checkout URL");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("Checkout error");
      }
    } finally {
      setLoading(null);
    }
  };

  const requestConsulting = () => {
    const subject = encodeURIComponent(t("billing_contact_subject"));
    const body = encodeURIComponent(
      lang === "es"
        ? `Hola,\n\nMe interesa el plan Consultoría + herramienta (500€/mes). ¿Podemos hablar?\n\nGracias,\n`
        : `Hi,\n\nI'm interested in the Consulting + tool plan (€500/mo). Can we talk?\n\nThanks,\n`
    );
    window.location.href = `mailto:${t("contact_email")}?subject=${subject}&body=${body}`;
  };

  const currentPlan = meBilling?.plan;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <Header />
      <main data-testid="pricing-page" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Hero */}
        <section className="max-w-3xl mb-16">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[#D90429] mb-4">
            {t("pricing_hero_eyebrow")}
          </div>
          <h1
            data-testid="pricing-title"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-5"
          >
            {t("pricing_hero_title")}
          </h1>
          <p className="text-base md:text-lg text-[#404040] font-serif-display italic leading-snug">
            {t("pricing_hero_lead")}
          </p>
        </section>

        {/* Plans grid */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="plans-grid">
          <PlanCard
            planId="beta"
            label={t("plan_beta_name")}
            tagline={t("plan_beta_tagline")}
            price={49}
            recommended={false}
            cta={loading === "beta" ? t("plan_cta_loading") : t("plan_cta_subscribe")}
            ctaTestId="plan-beta-cta"
            isCurrent={currentPlan === "beta"}
            onAction={() => startCheckout("beta")}
          />
          <PlanCard
            planId="professional"
            label={t("plan_pro_name")}
            tagline={t("plan_pro_tagline")}
            price={149}
            recommended={true}
            cta={loading === "professional" ? t("plan_cta_loading") : t("plan_cta_subscribe")}
            ctaTestId="plan-professional-cta"
            isCurrent={currentPlan === "professional"}
            onAction={() => startCheckout("professional")}
          />
          <PlanCard
            planId="consulting"
            label={t("plan_consulting_name")}
            tagline={t("plan_consulting_tagline")}
            price={500}
            recommended={false}
            cta={t("plan_cta_contact")}
            ctaTestId="plan-consulting-cta"
            isCurrent={currentPlan === "consulting"}
            onAction={requestConsulting}
          />
        </section>

        {/* Legal note */}
        <p
          data-testid="pricing-legal"
          className="text-xs font-mono-tech tracking-wide text-[#888] max-w-3xl mb-16 leading-relaxed"
        >
          {t("pricing_legal")}
        </p>

        {/* FAQ */}
        <section className="border-t border-[#E5E5E5] pt-12" data-testid="pricing-faq">
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold mb-8">
            {t("pricing_faq_title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {["1", "2", "3", "4"].map((n) => (
              <div key={n} data-testid={`pricing-faq-${n}`}>
                <h3 className="font-serif-display text-lg font-bold mb-2 flex items-start gap-2">
                  <span className="text-[#D90429] font-mono-tech text-xs mt-2">·</span>
                  {t(`pricing_faq_q${n}`)}
                </h3>
                <p className="text-sm text-[#404040] leading-relaxed pl-4">
                  {t(`pricing_faq_a${n}`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA when authenticated AND has an actual subscription tier */}
        {user && currentPlan && currentPlan !== "free" && (
          <section className="mt-16 p-8 bg-[#0A0A0A] text-white flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[#D90429] mb-2">
                · {t("billing_title")}
              </div>
              <div className="font-serif-display text-2xl">
                {t(`plan_${currentPlan}_name`) || currentPlan}
                {meBilling?.is_premium && (
                  <span className="ml-3 font-mono-tech text-xs tracking-widest text-[#7CFC00]">
                    · {t("billing_status_active").toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <Link
              to="/app/billing"
              data-testid="pricing-go-billing"
              className="px-6 py-3 border border-white hover:bg-white hover:text-[#0A0A0A] font-mono-tech text-xs uppercase tracking-[0.2em]"
            >
              {t("billing_manage")} →
            </Link>
          </section>
        )}
      </main>
    </div>
  );
}
